import { Box, Flex, Icon, IconButton, Skeleton, Text } from '@chakra-ui/react';
import type { VoucherGetEligibilitiesItem } from '@diamond/shared/types';
import {
  buildRewardForProduct,
  extractRewardFromTitle,
  formatMagnifiedQty,
  formatQty,
} from '@diamond/sol-buyer/utils';
import { useVoucherDetail } from '@diamond/sol-buyer-context';
import { AddShoppingCartOutlined, CheckCircle } from '@mui/icons-material';
import { memo } from 'react';

import { VoucherIcon } from '../../carts/Icons';

interface VoucherSectionProps {
  vouchers: VoucherGetEligibilitiesItem[];
  itemCode?: number;
  effectiveMagnifiedQty: number;
  maxAllowedQuantity: number;
  onUseQuantity: (qty: number) => void;
}

interface VoucherRowProps {
  voucher: VoucherGetEligibilitiesItem;
  itemCode?: number;
  isActive: boolean;
  canUse: boolean;
  voucherTargetQty: number | undefined;
  ruleQtyLabel: string | undefined;
  onUseQuantity: (qty: number) => void;
}

const VoucherRow = memo(function VoucherRow({
  voucher,
  itemCode,
  isActive,
  canUse,
  voucherTargetQty,
  ruleQtyLabel: fallbackQtyLabel,
  onUseQuantity,
}: VoucherRowProps) {
  const { data: detail, isLoading } = useVoucherDetail(voucher.code);

  // Both rule qty and reward come from the detail response (term_messages +
  // reward_products). The list endpoint's message_rule_products is used only
  // as a synchronous fallback while the detail call is in-flight.
  const detailRuleProduct = detail?.term_messages
    ?.flatMap((t) => t.products ?? [])
    .find((p) => p.product_item_code === itemCode);
  const ruleQtyLabel =
    detailRuleProduct?.min_quantity != null
      ? formatMagnifiedQty(detailRuleProduct.min_quantity)
      : fallbackQtyLabel;

  const structuredReward = buildRewardForProduct(detail, itemCode);
  const titleReward = extractRewardFromTitle(voucher.title);
  const reward = structuredReward ?? titleReward;

  const hasRuleQty = ruleQtyLabel !== undefined;
  const showRewardSkeleton = isLoading && !titleReward;
  const showReward = !showRewardSkeleton && !!reward;

  const handleRowClick = canUse && voucherTargetQty
    ? () => onUseQuantity(voucherTargetQty)
    : undefined;

  return (
    <Flex
      align="center"
      gap={2}
      bgColor={isActive ? '#F1FFF1' : '#F1F8FF'}
      rounded="md"
      px={3}
      py={2}
      cursor={handleRowClick ? 'pointer' : 'default'}
      onClick={handleRowClick}
      transition="background-color 0.15s ease"
      _hover={
        handleRowClick
          ? { bgColor: '#E4EEFF' }
          : isActive
          ? { bgColor: '#E3F8E3' }
          : undefined
      }
    >
      <Box flexShrink={0}>
        <VoucherIcon boxSize={5} color="#34C759" />
      </Box>
      <Box flex="1" minW={0}>
        <Text fontSize="sm" noOfLines={2}>
          {hasRuleQty && (
            <Text as="span" color="#00003D" fontWeight="medium">
              Beli {ruleQtyLabel}
            </Text>
          )}
          {!hasRuleQty && !showRewardSkeleton && !reward && (
            <Text as="span" color="#00003D" fontWeight="medium">
              {voucher.title}
            </Text>
          )}
          {showRewardSkeleton && (
            <Skeleton
              display="inline-block"
              ml={hasRuleQty ? 2 : 0}
              height="14px"
              width="6rem"
              verticalAlign="middle"
            />
          )}
          {showReward && (
            <Text as="span" color="#FF0000" fontWeight="semibold">
              {hasRuleQty ? ` ${reward}` : reward}
            </Text>
          )}
        </Text>
      </Box>
      {isActive ? (
        <Icon as={CheckCircle} boxSize={5} color="#34C759" flexShrink={0} />
      ) : canUse ? (
        <IconButton
          aria-label={`pakai qty voucher ${voucher.title}`}
          icon={<Icon as={AddShoppingCartOutlined} boxSize={5} />}
          variant="ghost"
          color="#0000A3"
          size="sm"
          // Row already owns the click; button stays for visual affordance.
          // Stop propagation so we don't fire onUseQuantity twice.
          onClick={(e) => {
            e.stopPropagation();
            if (voucherTargetQty) onUseQuantity(voucherTargetQty);
          }}
        />
      ) : null}
    </Flex>
  );
});

export function VoucherSection({
  vouchers,
  itemCode,
  effectiveMagnifiedQty,
  maxAllowedQuantity,
  onUseQuantity,
}: VoucherSectionProps) {
  if (vouchers.length === 0) return null;

  const rows = vouchers.map((voucher) => {
    const ruleProduct = voucher.message_rule_products
      ?.flatMap((r) => r.products ?? [])
      .find((p) => p.product_item_code === itemCode);
    const voucherTargetQty =
      ruleProduct?.min_quantity != null
        ? ruleProduct.min_quantity * 1000
        : undefined;
    const isActive =
      !ruleProduct ||
      (voucherTargetQty !== undefined &&
        voucherTargetQty <= effectiveMagnifiedQty);
    const canUse =
      voucherTargetQty !== undefined &&
      voucherTargetQty <= maxAllowedQuantity * 1000 &&
      !isActive;
    const ruleQtyLabel =
      ruleProduct?.min_quantity != null
        ? formatQty(ruleProduct.min_quantity)
        : undefined;
    return { voucher, voucherTargetQty, isActive, canUse, ruleQtyLabel };
  });

  const activeCount = rows.reduce(
    (acc, r) => (r.isActive ? acc + 1 : acc),
    0
  );
  const hasActive = activeCount > 0;
  const hasMultipleActive = activeCount > 1;

  return (
    <Box mb={4}>
      <Text fontSize="xs" fontWeight="semibold" color="#00003D" mb={2}>
        Voucher Tersedia
      </Text>
      <Flex direction="column" gap={2}>
        {rows.map((row) => (
          <VoucherRow
            key={row.voucher.id}
            voucher={row.voucher}
            itemCode={itemCode}
            isActive={row.isActive}
            canUse={row.canUse}
            voucherTargetQty={row.voucherTargetQty}
            ruleQtyLabel={row.ruleQtyLabel}
            onUseQuantity={onUseQuantity}
          />
        ))}
      </Flex>
      {hasActive && (
        <Text fontSize="xs" color="#006BD1" mt={2}>
          Syarat terpenuhi, Aktifkan voucher di Keranjang!
        </Text>
      )}
      {hasMultipleActive && (
        <Text fontSize="xs" color="#006BD1" mt={1}>
          Hanya bisa menggunakan 1 voucher
        </Text>
      )}
    </Box>
  );
}
